import React, { useContext, useState } from 'react'
import moment from 'moment'
import startOfToday from 'date-fns/startOfToday'
import { Button, DatePicker, Form, Input, message, Select } from 'antd'

import { ErpClientContext } from '../../services/erp'
import { toISOStringWithTimezone } from '../../utils'
import {
  ALL_COUNTRIES,
  FREE_SHIPPING_RATES,
  SELLING_COUNTRIES,
} from '../../consts'

import 'antd/dist/antd.css'

const initial = {
  paises: [],
  tarifa: '',
  cart_amount: '',
  min_precio_uni: '',
}

const countries = SELLING_COUNTRIES.map((country) => {
  return { code: country, name: ALL_COUNTRIES[country] }
})

const sortedCountries = Array.from(countries).sort((a, b) => {
  const nameA = a.name.toLowerCase()
  const nameB = b.name.toLowerCase()
  if (nameA < nameB) {
    return -1
  }
  if (nameA > nameB) {
    return 1
  }
  return 0
})

export function CreateFreeShippingRatesForm({
  onClose,
  onCreateFreeShippingRates,
}) {
  const [form] = Form.useForm()
  const erpService = useContext(ErpClientContext)
  const [status, setStatus] = useState({})
  const [state, setState] = useState(initial)

  async function onFinish() {
    setStatus({ type: 'creating' })
    const freeShippingRate = { ...state }

    const isOk = await erpService
      .createFreeShippingRate(freeShippingRate)
      .catch((e) => {
        if (e.message.includes('overlapping_date_exists')) {
          setStatus({
            type: 'error',
            message:
              'There is an existing free shipping rate overlapping the same date',
          })
          return
        }

        setStatus({
          type: 'error',
          message: 'There is an error creating the free shipping rate',
        })
      })

    if (isOk) {
      message.success(`Shipping rate(s) created`, 2.5)
      onClose()
      onCreateFreeShippingRates()
    }
  }

  const onValuesChange = (changedValues, allValues) => {
    const { tarifa, cart_amount, min_precio_uni, duration } = allValues

    // 'cart_amount' and 'min_precio_uni' fields must always be sent with a value of 0 if tarifa is 'dtp'
    const cartAmount = tarifa === 'dtp' ? '0' : cart_amount
    const minPrecioUni = tarifa === 'dtp' ? '0' : min_precio_uni

    // Dates
    const [startDate, endDate] = duration
      ? [
          toISOStringWithTimezone(duration[0]),
          toISOStringWithTimezone(duration[1]),
        ]
      : [undefined, undefined]

    setState((s) => ({
      ...s,
      ...allValues,
      cart_amount: cartAmount,
      min_precio_uni: minPrecioUni,
      desde: startDate,
      hasta: endDate,
    }))
  }

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        className="free-shipping-rates-form"
        initialValues={initial}
        onFinish={onFinish}
        onValuesChange={onValuesChange}
      >
        <Form.Item
          label="Countries"
          name="paises"
          rules={[
            {
              required: true,
              message: 'Enter the countries',
            },
          ]}
        >
          <Select
            mode="multiple"
            allowClear
            placeholder="Select one or more countries"
          >
            {sortedCountries.map((country) => (
              <Select.Option key={country.code} value={country.code}>
                {country.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Shipping tariff rate"
          name="tarifa"
          rules={[
            {
              required: true,
              message: 'Enter the shipping tariff rate',
            },
          ]}
        >
          <Select placeholder="Select an option">
            {Object.entries(FREE_SHIPPING_RATES).map((freeShippingRate) => (
              <Select.Option
                key={freeShippingRate[0]}
                value={freeShippingRate[0]}
              >
                {freeShippingRate[1]}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {state.tarifa && state.tarifa !== 'dtp' && (
          <>
            <Form.Item
              label="Minimum sum of products (with taxes)"
              name="cart_amount"
              rules={[
                {
                  required: true,
                  message: 'Enter the minimum sum of products',
                },
              ]}
            >
              <Input placeholder="A positive number" />
            </Form.Item>

            <Form.Item
              label="Minimum product price (with taxes)"
              name="min_precio_uni"
              rules={[
                {
                  required: true,
                  message: 'Enter the minimum product price',
                },
              ]}
            >
              <Input placeholder="A positive number" />
            </Form.Item>
          </>
        )}

        <Form.Item
          label="Duration"
          name="duration"
          rules={[
            {
              required: true,
              message: 'Enter the duration',
            },
            () => ({
              validator(rule, value) {
                if (
                  !value ||
                  new Date(value[0].toISOString()).getTime() >=
                    startOfToday().getTime()
                ) {
                  return Promise.resolve()
                }
                return Promise.reject(
                  `The free shipping rate shouldn't be expired`
                )
              },
            }),
          ]}
        >
          <DatePicker.RangePicker
            showTime={{
              defaultValue: [
                moment('00:00:00', 'HH:mm:ss'),
                moment('23:59:59', 'HH:mm:ss'),
              ],
            }}
          />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            disabled={status.type === 'creating'}
          >
            Create
          </Button>
        </Form.Item>

        {status.type === 'error' && (
          <div style={{ color: 'red' }}>{status.message}</div>
        )}
      </Form>
    </>
  )
}
